// Looks up a place on OpenStreetMap's Nominatim, only when someone asks (the "Search
// OpenStreetMap" item under a place field): its usage policy forbids searching as people type.
// The policy also asks for at most one request a second, a cache, and credit to OpenStreetMap
// (shown under the results). The browser's Referer names the app, as the policy asks.
// https://operations.osmfoundation.org/policies/nominatim/

import type { PlaceLocation } from '../model/types';

export interface FoundPlace {
  /** "Springfield, Sangamon County, Illinois, United States", ready for a place field. */
  label: string;
  /** What it is: "City", "Address", "Hospital". */
  type: string;
  location: PlaceLocation;
}

/** The parts of a Nominatim `jsonv2` result with `addressdetails=1` that are used here. */
export interface NominatimResult {
  lat?: string;
  lon?: string;
  name?: string;
  type?: string;
  addresstype?: string;
  address?: Record<string, string>;
}

const ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const SETTLEMENT = ['city', 'town', 'village', 'hamlet', 'municipality'];
const TYPES: Record<string, string> = { house: 'Address', road: 'Street', neighbourhood: 'Neighborhood', suburb: 'Neighborhood', quarter: 'Neighborhood', city_district: 'District' };

/** Genealogy order, smallest first: name, street address, town, county, state, country. */
export function placeLabel(r: NominatimResult): string {
  const a = r.address ?? {};
  const street = [a.house_number, a.road].filter(Boolean).join(' ');
  const parts = [r.name, street, SETTLEMENT.map((k) => a[k]).find(Boolean), a.county, a.state, a.country];
  const out: string[] = [];
  for (const p of parts) {
    const t = p?.trim();
    if (t && !out.includes(t)) out.push(t);
  }
  return out.join(', ');
}

// For these, `addresstype` says what it is; for anything else (a hospital, a station) it's just
// the map category ("amenity", "railway"), and `type` says what it is.
const AREAS = new Set(['city', 'town', 'village', 'hamlet', 'municipality', 'borough', 'suburb', 'neighbourhood', 'quarter', 'city_district', 'district', 'county', 'state', 'province', 'region', 'country', 'island', 'road', 'house']);

export function placeType(r: NominatimResult): string {
  const t = r.addresstype && AREAS.has(r.addresstype) ? r.addresstype : r.type;
  if (!t || t === 'yes') return '';
  return TYPES[t] ?? (t.charAt(0).toUpperCase() + t.slice(1)).replace(/_/g, ' ');
}

const cache = new Map<string, FoundPlace[]>();
let lastRequest = 0;

/** Up to five places matching `query`. Throws when offline or the search fails. */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<FoundPlace[]> {
  const q = query.trim().replace(/\s+/g, ' ');
  const key = q.toLowerCase();
  const hit = cache.get(key);
  if (hit) return hit;
  // One request a second, however often it's asked for.
  const wait = lastRequest + 1000 - Date.now();
  if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
  signal?.throwIfAborted();
  lastRequest = Date.now();
  const params = new URLSearchParams({ q, format: 'jsonv2', addressdetails: '1', limit: '5', 'accept-language': 'en' });
  const res = await fetch(`${ENDPOINT}?${params}`, { signal });
  if (!res.ok) throw new Error(`Nominatim answered ${res.status}`);
  const found: FoundPlace[] = [];
  for (const r of (await res.json()) as NominatimResult[]) {
    const label = placeLabel(r);
    const location = { lat: Number(r.lat), lon: Number(r.lon) };
    if (label && isFinite(location.lat) && isFinite(location.lon) && !found.some((f) => f.label === label)) found.push({ label, type: placeType(r), location });
  }
  cache.set(key, found);
  return found;
}
